/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
   indexCatalogById,
   suggestedDurationMinutes,
} from '../RouteManagement/serviceFields';
import { IconButton } from '../../../styles/ui/Button';

// =============================================================================
// ServiceSelector (handoff 0039 §3) — seletor CONTROLADO de serviços do catálogo
// unificado para um item de roteirização. Usado na criação manual
// (ManualEntryTab), na ficha de edição (ItemEditFormPanel) e espelha o modal do
// board (ServiceClassificationModal).
//
// É replace-set: `value` é o array de seleção; ADICIONAR escolhe um serviço
// ativo do catálogo (quantidade 1), e os steppers/remover editam a lista. Cada
// entrada: { commission_table_id, service_code, service_description, quantity }.
//
// Mostra o tempo sugerido (Σ quantity × tempo do catálogo) — informativo; o
// backend recalcula a duração no save. Não trata override de duração (isso vive
// só no modal do board, que precisa dele para o dia do roteiro).
// =============================================================================

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const AddRow = styled.div`
   select {
      width: 100%;
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      font-family: inherit;
      background: #fff;
   }
`;

const SelectedList = styled.div`
   display: flex;
   flex-direction: column;
   border: 1px solid rgba(0, 0, 0, 0.12);
   border-radius: 10px;
   overflow: hidden;
`;

const ServiceRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 9px 12px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.06);

   &:last-of-type {
      border-bottom: 0;
   }
`;

const ServiceLabel = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;

   .nm {
      font-weight: 600;
      color: #1f1f18;
      font-size: 0.84rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }
   .std {
      font-size: 0.7rem;
      color: #8b8b82;
   }
   .inactive {
      color: #b45309;
   }
`;

const RowRight = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-shrink: 0;
`;

const Stepper = styled.div`
   display: inline-flex;
   align-items: center;
   border: 1px solid rgba(0, 0, 0, 0.15);
   border-radius: 8px;
   overflow: hidden;

   button {
      width: 28px;
      height: 28px;
      border: 0;
      background: #f5f5f3;
      cursor: pointer;
      font-size: 1.05rem;
      line-height: 1;
      color: #1f1f18;

      &:hover:not(:disabled) {
         background: #ececde;
      }
      &:disabled {
         opacity: 0.4;
         cursor: not-allowed;
      }
   }

   input {
      width: 42px;
      height: 28px;
      border: 0;
      border-left: 1px solid rgba(0, 0, 0, 0.12);
      border-right: 1px solid rgba(0, 0, 0, 0.12);
      text-align: center;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 0.82rem;
      -moz-appearance: textfield;
   }
   input::-webkit-outer-spin-button,
   input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
   }
`;

const RemoveButton = styled(IconButton).attrs({ $intent: 'danger' })`
   width: 28px;
   height: 28px;
   font-size: 1.05rem;
   color: ${({ theme }) => theme.colors.danger};
`;

const EmptyBox = styled.div`
   padding: 12px;
   font-size: 0.76rem;
   color: #8b8b82;
   font-style: italic;
   text-align: center;
`;

const DurationLine = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   font-size: 0.76rem;
   color: #44443d;
   padding: 2px 2px 0;

   strong {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      color: #1f1f18;
   }
`;

function clampQty(value) {
   const n = Math.trunc(Number(value));
   if (Number.isNaN(n) || n < 1) return 1;
   return Math.min(n, 999);
}

export default function ServiceSelector({
   value = [],
   onChange,
   catalog = [],
   disabled = false,
   showDuration = true,
}) {
   const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);
   const catalogById = useMemo(() => indexCatalogById(catalog), [catalog]);

   const selectedIds = useMemo(
      () => new Set(selected.map((s) => s.commission_table_id)),
      [selected]
   );
   const addOptions = useMemo(
      () =>
         (catalog || []).filter(
            (e) => e.is_active !== false && !selectedIds.has(e.id)
         ),
      [catalog, selectedIds]
   );
   const suggestion = useMemo(
      () => suggestedDurationMinutes(selected, catalogById),
      [selected, catalogById]
   );

   const handleAdd = (e) => {
      const id = Number(e.target.value);
      if (!id) return;
      const entry = catalogById[id];
      if (!entry || selectedIds.has(id)) return;
      onChange([
         ...selected,
         {
            commission_table_id: entry.id,
            service_code: entry.service_code,
            service_description: entry.service_description,
            quantity: 1,
         },
      ]);
   };

   const setQty = (id, qty) =>
      onChange(
         selected.map((s) =>
            s.commission_table_id === id
               ? { ...s, quantity: clampQty(qty) }
               : s
         )
      );
   const bumpQty = (id, delta) =>
      onChange(
         selected.map((s) =>
            s.commission_table_id === id
               ? { ...s, quantity: clampQty((s.quantity || 0) + delta) }
               : s
         )
      );
   const remove = (id) =>
      onChange(selected.filter((s) => s.commission_table_id !== id));

   return (
      <Wrap>
         <AddRow>
            {/* eslint-disable-next-line jsx-a11y/no-onchange */}
            <select
               value=""
               onChange={handleAdd}
               disabled={disabled || addOptions.length === 0}
               aria-label="Adicionar serviço do catálogo"
            >
               <option value="">
                  {addOptions.length === 0
                     ? 'Nenhum serviço disponível para adicionar'
                     : '+ Adicionar serviço do catálogo…'}
               </option>
               {addOptions.map((e) => (
                  <option key={e.id} value={e.id}>
                     {e.service_code} — {e.service_description}
                  </option>
               ))}
            </select>
         </AddRow>

         <SelectedList>
            {selected.length === 0 ? (
               <EmptyBox>Nenhum serviço atribuído.</EmptyBox>
            ) : (
               selected.map((s) => {
                  const entry = catalogById[s.commission_table_id];
                  const time = entry?.estimated_completion_service_time;
                  const isInactive = entry ? entry.is_active === false : true;
                  return (
                     <ServiceRow key={s.commission_table_id}>
                        <ServiceLabel>
                           <span className="nm">{s.service_description}</span>
                           <span className="std">
                              {s.service_code}
                              {time != null
                                 ? ` · ${time} min/un`
                                 : ' · tempo não definido'}
                              {isInactive && (
                                 <span className="inactive"> · inativo</span>
                              )}
                           </span>
                        </ServiceLabel>
                        <RowRight>
                           <Stepper>
                              <button
                                 type="button"
                                 onClick={() =>
                                    bumpQty(s.commission_table_id, -1)
                                 }
                                 disabled={disabled || s.quantity <= 1}
                                 aria-label={`Diminuir ${s.service_description}`}
                              >
                                 −
                              </button>
                              <input
                                 type="number"
                                 min="1"
                                 value={s.quantity}
                                 onChange={(e) =>
                                    setQty(s.commission_table_id, e.target.value)
                                 }
                                 disabled={disabled}
                                 aria-label={s.service_description}
                              />
                              <button
                                 type="button"
                                 onClick={() =>
                                    bumpQty(s.commission_table_id, 1)
                                 }
                                 disabled={disabled}
                                 aria-label={`Aumentar ${s.service_description}`}
                              >
                                 +
                              </button>
                           </Stepper>
                           <RemoveButton
                              type="button"
                              onClick={() => remove(s.commission_table_id)}
                              disabled={disabled}
                              aria-label={`Remover ${s.service_description}`}
                              title="Remover serviço"
                           >
                              ×
                           </RemoveButton>
                        </RowRight>
                     </ServiceRow>
                  );
               })
            )}
         </SelectedList>

         {showDuration && selected.length > 0 && (
            <DurationLine>
               <span>Tempo estimado (catálogo)</span>
               <strong>{suggestion} min</strong>
            </DurationLine>
         )}
      </Wrap>
   );
}

ServiceSelector.propTypes = {
   value: PropTypes.array,
   onChange: PropTypes.func.isRequired,
   catalog: PropTypes.array,
   disabled: PropTypes.bool,
   showDuration: PropTypes.bool,
};
